import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { softTick } from '../utils/haptics';
import { TIME_MARKERS, getTimeFromPosition, formatTime, getPositionFromTime, getCurrentTime } from '../utils/timeHelpers';

/**
 * TimePickerSlider - Minimalist 24-hour time picker for Discovery Dial
 * 
 * Positioned on center-right edge, provides vertical scrubbing gesture
 * for precise time selection (12:00 AM to 11:30 PM in 30-minute increments).
 * 
 * @param {Object} props
 * @param {{ hours: number, minutes: number }} props.selectedTime - Current selected time
 * @param {Function} props.onTimeChange - Callback when time changes
 */
export default function TimePickerSlider({ selectedTime, onTimeChange }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [showPersistentDisplay, setShowPersistentDisplay] = useState(false);
  const [currentTime, setCurrentTime] = useState(getCurrentTime());
  const containerRef = useRef(null);
  const prevSlotRef = useRef(0);
  const persistentDisplayTimeoutRef = useRef(null);

  // Update current time every minute
  useEffect(() => {
    const updateCurrentTime = () => {
      setCurrentTime(getCurrentTime());
    };
    
    const interval = setInterval(updateCurrentTime, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  /**
   * Handle pointer down - start scrubbing
   */
  const handlePointerDown = useCallback((e) => {
    e.stopPropagation(); // Prevent dial gestures
    setIsDragging(true);
    updateTimeFromPosition(e.clientY);
  }, []);

  /**
   * Handle pointer move - continue scrubbing
   */
  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    e.stopPropagation(); // Prevent dial gestures
    updateTimeFromPosition(e.clientY);
  }, [isDragging]);

  /**
   * Handle pointer up - end scrubbing
   */
  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    prevSlotRef.current = 0;
    
    // Show persistent display for 1.5 seconds
    setShowPersistentDisplay(true);
    if (persistentDisplayTimeoutRef.current) {
      clearTimeout(persistentDisplayTimeoutRef.current);
    }
    persistentDisplayTimeoutRef.current = setTimeout(() => {
      setShowPersistentDisplay(false);
    }, 1500);
  }, []);

  /**
   * Update time based on vertical position
   */
  const updateTimeFromPosition = useCallback((clientY) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const { hours, minutes, slotIndex } = getTimeFromPosition(
      clientY,
      rect.top,
      rect.height
    );

    // Haptic feedback on slot change (every 30 minutes)
    if (slotIndex !== prevSlotRef.current) {
      softTick();
      prevSlotRef.current = slotIndex;
    }

    setDragY(clientY - rect.top);
    onTimeChange({ hours, minutes });
  }, [onTimeChange]);

  /**
   * Handle tap on marker - jump to that time
   */
  const handleMarkerTap = useCallback((marker) => {
    onTimeChange({ hours: marker.hours, minutes: marker.minutes });
    softTick();
  }, [onTimeChange]);

  /**
   * Calculate persistent marker position from selectedTime
   */
  const selectedTimePosition = useMemo(() => {
    if (!containerRef.current) return 0;
    const fraction = getPositionFromTime(selectedTime.hours, selectedTime.minutes);
    return fraction * 240; // 240px is the container height
  }, [selectedTime]);

  return (
    <div
      ref={containerRef}
      className="time-picker-slider"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        position: 'fixed',
        right: 'max(20px, env(safe-area-inset-right))', // Increased from 8px to 20px for more space
        top: '20%', // MOVED UP from 50% for better mobile visibility
        transform: 'translateY(-20%)',
        height: 'clamp(220px, 35vh, 300px)', // Slightly taller for easier interaction
        width: 'clamp(50px, 12vw, 70px)', // Wider for easier tap targets
        minWidth: '50px', // Increased minimum width
        maxWidth: 'calc(100vw - 80px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000, // Increased z-index to ensure it's on top
        touchAction: 'none',
        userSelect: 'none',
        paddingTop: 'max(8px, env(safe-area-inset-top))', // Increased padding
        paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
        paddingLeft: '12px', // Added horizontal padding for breathing room
        paddingRight: '12px',
        // Subtle background for better visibility and touch area definition
        background: 'rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderRadius: '28px', // Rounded edges for modern look
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
        // Smooth transition for hover effects
        transition: 'all 0.2s ease'
      }}
    >
      {/* Time markers */}
      {TIME_MARKERS.map((marker) => {
        const isActive = 
          selectedTime.hours === marker.hours && 
          selectedTime.minutes === marker.minutes;

        return (
          <motion.div
            key={marker.label}
            onClick={() => handleMarkerTap(marker)}
            animate={isActive ? { scale: 1.15 } : { scale: 1 }}
            whileHover={{ scale: 1.05 }} // Added hover effect
            whileTap={{ scale: 0.95 }} // Added tap feedback
            transition={{ duration: 0.2 }}
            style={{
              fontSize: isActive ? 'clamp(18px, 4vw, 20px)' : 'clamp(14px, 3vw, 16px)', // Much larger for selected
              fontWeight: isActive ? '800' : '500', // Much bolder for selected
              color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.6)', // High contrast
              opacity: isActive ? 1 : 0.7,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              textAlign: 'center',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              pointerEvents: 'auto',
              minWidth: '56px', // Larger touch target
              minHeight: '56px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Enhanced background for better visibility
              background: isActive 
                ? 'rgba(0, 212, 255, 0.25)' 
                : 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: isActive 
                ? '2px solid rgba(0, 212, 255, 0.6)' 
                : '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: isActive 
                ? '0 0 20px rgba(0, 212, 255, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)' 
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
              WebkitTapHighlightColor: 'transparent' // Remove default tap highlight
            }}
          >
            {marker.label}
          </motion.div>
        );
      })}

      {/* Floating time bubble during drag */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              left: '-70px',
              top: `${dragY}px`,
              transform: 'translateY(-50%)',
              background: 'rgba(0, 0, 0, 0.95)',
              color: 'white',
              padding: '8px 14px',
              borderRadius: '20px',
              fontSize: '15px',
              fontWeight: '700',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}
          >
            {formatTime(selectedTime.hours, selectedTime.minutes)}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scrub indicator line (subtle visual aid) */}
      {isDragging && (
        <div
          style={{
            position: 'absolute',
            left: '-4px',
            top: `${dragY}px`,
            width: '52px',
            height: '2px',
            background: 'rgba(100, 150, 255, 0.6)',
            borderRadius: '1px',
            pointerEvents: 'none',
            transform: 'translateY(-50%)'
          }}
        />
      )}

      {/* ENHANCED PERSISTENT MARKER - Shows selected time position */}
      <motion.div
        animate={{ 
          top: `${selectedTimePosition}px`,
          scale: isDragging ? 1.3 : [1, 1.2, 1],
          opacity: [0.8, 1, 0.8]
        }}
        transition={{ 
          type: 'spring', 
          stiffness: 300, 
          damping: 25,
          duration: 0.2,
          scale: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          },
          opacity: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }
        }}
        style={{
          position: 'absolute',
          right: '0px',
          transform: 'translateY(-50%)',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
          border: '3px solid rgba(255, 255, 255, 0.9)',
          boxShadow: '0 0 20px rgba(0, 212, 255, 0.6), 0 4px 12px rgba(0, 0, 0, 0.3)',
          pointerEvents: 'none',
          zIndex: 100
        }}
      />

      {/* SUBTLE TRACK LINE - Shows full range */}
      <div
        style={{
          position: 'absolute',
          right: '4px',
          top: '0',
          width: '2px',
          height: '100%',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '1px',
          pointerEvents: 'none',
          zIndex: 1
        }}
      />

      {/* CURRENT TIME INDICATOR - Shows "NOW" position */}
      {(() => {
        const currentTimePosition = getPositionFromTime(currentTime.hours, currentTime.minutes) * 240;
        return (
          <motion.div
            animate={{ 
              top: `${currentTimePosition}px`,
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              scale: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }
            }}
            style={{
              position: 'absolute',
              right: '8px',
              transform: 'translateY(-50%)',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#ff6b35',
              border: '2px solid white',
              boxShadow: '0 0 15px rgba(255, 107, 53, 0.6), 0 2px 8px rgba(0, 0, 0, 0.3)',
              pointerEvents: 'none',
              zIndex: 50
            }}
          />
        );
      })()}

      {/* PERSISTENT TIME DISPLAY - Shows current selection */}
      <AnimatePresence>
        {showPersistentDisplay && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ 
              type: 'spring', 
              stiffness: 300, 
              damping: 25,
              duration: 0.3 
            }}
            style={{
              position: 'fixed',
              top: '50%',
              right: '120px', // Position to the left of the time slider
              transform: 'translateY(-50%)',
              background: 'rgba(0, 0, 0, 0.95)',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '16px',
              fontSize: '18px',
              fontWeight: '700',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 212, 255, 0.3)',
              border: '2px solid rgba(0, 212, 255, 0.6)',
              zIndex: 1000,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '0.5px',
              textAlign: 'center',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              pointerEvents: 'none',
              maxWidth: '200px',
              whiteSpace: 'nowrap'
            }}
          >
            {formatTime(selectedTime.hours, selectedTime.minutes)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

